import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import CustomButton from "components/shared/Buttons/CustomButton";
import theme from "components/utils/theme";
import { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_STRIPE_PAYMENT } from "../../../redux/types/subscription.types";
import forms from '../../shared/Forms/forms.module.scss';
import text from '../../shared/css/text.module.scss';
import styles from "./pricingPlan.module.scss";
import { BILLING_CYCLES } from "./helper";
import { subscriptionActions } from "../../../redux/slice/subscription.slice";
import { useNavigate } from "react-router-dom";
import { verifyCouponServ } from "../../../redux/services/subscription.service";

const PaymentForm = ({ selectedPlan, isYearly }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const subscriptionDiscount = useSelector(subscriptionActions.selectors.getSubscriptionDiscount)
    const [couponCode, setCouponCode] = useState('');
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponMessage, setCouponMessage] = useState('');

    const handlePayment = async (e) => {
        e.preventDefault();

        if (!stripe || !elements) {
            toast.error("Something went wrong. Please try again")
            return;
        }

        setLoading(true);
        const cardNumberElement = elements.getElement(CardNumberElement);

        try {
            const { paymentMethod, error } = await stripe.createPaymentMethod({
                type: "card",
                card: cardNumberElement,
            });

            if (error?.code === "card_declined") {
                return toast.error(error?.decline_code === "insufficient_funds"
                    ? "Your card was declined due to insufficient funds."
                    : "Your card was declined. Please use a different payment method.");
            }

            const payload = {
                planId: selectedPlan?._id,
                priceId: isYearly ? selectedPlan?.yearlyPriceId : selectedPlan?.monthlyPriceId,
                paymentMethodId: paymentMethod.id,
                billingCycle: isYearly ? BILLING_CYCLES.yearly : BILLING_CYCLES.monthly,
                ...(subscriptionDiscount?.couponCode && { couponCode: subscriptionDiscount.couponCode })
            };
            // const paymentResponse = await createStripePaymentServ(payload);
            dispatch({ type: CREATE_STRIPE_PAYMENT, payload, navigate })
        } catch (error) {
            console.error("Error during payment:", error);
            toast.error("Payment failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const formatAmount = (amount, currency = "CAD") => {
        if (amount == null) return null;
        return new Intl.NumberFormat("en-CA", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
        }).format(amount / 100);
    };

    const handleApplyCoupon = async () => {
        const normalizedCode = couponCode.trim();
        if (!normalizedCode) {
            setCouponMessage("Please enter a coupon code.");
            return;
        }
        if (subscriptionDiscount?.couponCode === normalizedCode.toUpperCase()) {
            setCouponMessage("Coupon already applied.");
            return;
        }
        setCouponMessage("");
        setCouponLoading(true);
        try {
            const couponResponse = await toast.promise(
                verifyCouponServ({ couponCode: normalizedCode }),
                {
                    loading: "Verifying coupon...",
                    success: (response) => response?.message || "Coupon code is valid.",
                    error: (err) => err?.response?.data?.message || "Coupon code is not valid.",
                }
            );
            const status = couponResponse?.status;
            const discount = couponResponse?.data?.discount;
            if (status !== 200 || !discount?.valid) {
                throw new Error(couponResponse?.message || "Coupon code is not valid.");
            }
            const discountPayload = {
                couponCode: normalizedCode.toUpperCase(),
                percentOff: discount?.percentOff,
                amountOff: discount?.amountOff,
                currency: discount?.currency ? discount.currency.toUpperCase() : null,
                duration: discount?.duration,
                durationInMonths: discount?.durationInMonths ?? discount?.duration_in_months,
                redeemBy: discount?.redeemBy ?? discount?.redeem_by,
                valid: Boolean(discount?.valid),
                discountPercentOff: discount?.percentOff ?? 0,
                discountAmountOff: discount?.amountOff,
            };
            dispatch(subscriptionActions.actions.setSubscriptionDiscount(discountPayload));
            setCouponMessage(couponResponse?.message || "Coupon applied successfully.");
        } catch (err) {
            dispatch(subscriptionActions.actions.setSubscriptionDiscount({}));
            setCouponMessage(err?.response?.data?.message || err?.message || "Coupon code is not valid.");
        } finally {
            setCouponLoading(false);
        }
    };

    const handleRemoveCoupon = () => {
        setCouponCode("");
        setCouponMessage("");
        dispatch(subscriptionActions.actions.setSubscriptionDiscount({}));
    };

    const couponDisplayLabel = subscriptionDiscount?.percentOff
        ? `${subscriptionDiscount.percentOff}% off`
        : subscriptionDiscount?.amountOff
            ? formatAmount(subscriptionDiscount.amountOff, subscriptionDiscount.currency)
            : null;


    return (
        <div className={styles.paymentFormContainer}>
            <Box className={styles.container}>
                <Typography variant="body1" fontWeight={'bold'}>Enter Billing Details</Typography>
                <br />
                <form onSubmit={handlePayment} className={styles.form}>
                    <div className={styles.couponSection}>
                        <div className={styles.couponInputWrapper}>
                            <label className={`${text.formLabel} ${forms.label}`}>Coupon code (optional)</label>
                            <input
                                type="text"
                                placeholder="Enter coupon code"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                                className={styles.couponInputField}
                            />
                        </div>
                        <div className={styles.couponAction}>
                            <CustomButton
                                type="button"
                                padding="10px 28px"
                                color={theme.palette.background.default}
                                bgColor={theme.palette.primary.main}
                                hover={theme.palette.info.main}
                                label={couponLoading ? <CircularProgress size={18} color={"inherit"} /> : "Apply"}
                                disabled={couponLoading}
                                onClick={handleApplyCoupon}
                            />
                        </div>
                    </div>
                    {couponMessage && (
                        <Typography variant="body2" className={`${styles.couponMessage} ${subscriptionDiscount?.valid ? styles.couponMessageSuccess : styles.couponMessageError}`}>
                            {couponMessage}
                        </Typography>
                    )}
                    {subscriptionDiscount?.valid && (
                        <div className={styles.couponSummary}>
                            <Typography variant="body2">
                                <strong>{couponDisplayLabel || "Coupon"}</strong> applied{
                                    subscriptionDiscount?.couponCode && (
                                        <> with <em>{subscriptionDiscount?.couponCode}</em></>
                                    )
                                }.
                            </Typography>
                            <button type="button" className={styles.couponRemove} onClick={handleRemoveCoupon}>
                                Remove
                            </button>
                        </div>
                    )}
                    <Grid container justifyContent={'space-between'}>
                        <Grid item xs={12} md={12} className={styles.paymentInput}>
                            <label className={`${text.formLabel} ${forms.label}`}>
                                Card Number
                            </label>
                            <div >
                                <CardNumberElement options={elementOptions} />
                            </div>
                        </Grid>
                        <Grid item xs={12} md={5.8} className={styles.paymentInput}>
                            <label className={`${text.formLabel} ${forms.label}`}>
                                Expiry Date
                            </label>
                            <CardExpiryElement options={elementOptions} />
                        </Grid>
                        <Grid item xs={12} md={5.8} className={styles.paymentInput}>
                            <label className={`${text.formLabel} ${forms.label}`}>
                                CVC
                            </label>
                            <CardCvcElement options={elementOptions} />
                        </Grid>
                        <Grid container item xs={12} md={12} justifyContent={'flex-end'}>
                            <CustomButton
                                padding={"0px 50px"}
                                color={theme.palette.background.default}
                                bgColor={theme.palette.primary.main}
                                hover={theme.palette.info.main}
                                label={loading ? <CircularProgress size={24} color={"inherit"} /> : "Pay"}
                                disabled={!stripe || loading}
                                type={"submit"}
                            />
                        </Grid>
                    </Grid>
                </form>
            </Box >
        </div >
    );
};

// Custom styles for Stripe's CardElement
const elementOptions = {
    style: {
        base: {
            fontSize: "16px",
            color: theme.palette.text.primary,
            "::placeholder": { color: theme.palette.gray.main },
        },
        invalid: {
            color: theme.palette.error.main,
        },
    },
};

export default PaymentForm;
